<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/conversations';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversationId: { type: [String, Number], required: true },
  agentName: { type: String, default: '' },
  agentTimezone: { type: String, default: '' },
  contactEmail: { type: String, default: '' },
});

const emit = defineEmits(['cancel', 'updated', 'update:show']);

const { t } = useI18n();
const isSaving = ref(false);
const isLoadingOptions = ref(false);
const options = ref({ picklists: {} });
const showOptionalFields = ref(false);
const mode = ref('create');
const crmModule = ref('');
const currentAppointment = ref(null);
const appointmentHistory = ref([]);
const appointment = reactive({
  event_title: '',
  email: '',
  date: '',
  time: '',
  status: 'Pendiente',
  appointment_type: 'Interna',
  generated_by: '',
  additional_fields: {},
  address: '',
  description: '',
  meeting_url: '',
});

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});
const hasCurrentAppointment = computed(() => Boolean(currentAppointment.value));
const isEditingExisting = computed(
  () => hasCurrentAppointment.value && mode.value === 'edit'
);
const isReprogramming = computed(() => mode.value === 'reprogram');
const appointmentTitle = computed(() => {
  if (isReprogramming.value) {
    return t('CONVERSATION.APPOINTMENT.REPROGRAM_TITLE');
  }
  if (crmModule.value === 'Leads') {
    return t('CONVERSATION.APPOINTMENT.CONVERT_TITLE');
  }
  if (hasCurrentAppointment.value) {
    return t('CONVERSATION.APPOINTMENT.EDIT_TITLE');
  }
  return t('CONVERSATION.APPOINTMENT.TITLE');
});
const requiredContactFields = computed(
  () => options.value.required_contact_fields || []
);
const optionalContactFields = computed(
  () => options.value.optional_contact_fields || []
);
const isFormFieldVisible = mappingKey =>
  !(options.value.hidden_form_fields || []).includes(mappingKey);

const resetForm = () => {
  mode.value = 'create';
  crmModule.value = '';
  currentAppointment.value = null;
  appointmentHistory.value = [];
  appointment.event_title = '';
  appointment.email = props.contactEmail;
  appointment.date = '';
  appointment.time = '';
  appointment.status = 'Pendiente';
  appointment.appointment_type = 'Interna';
  appointment.generated_by = '';
  appointment.additional_fields = {};
  appointment.address = '';
  appointment.description = '';
  appointment.meeting_url = '';
  showOptionalFields.value = false;
};

const loadAppointment = value => {
  Object.assign(appointment, {
    event_title: value.event_title || '',
    email: value.email || props.contactEmail,
    date: value.date || '',
    time: value.time || '',
    status: value.status || '',
    appointment_type: value.appointment_type || '',
    generated_by: value.generated_by || '',
    additional_fields: { ...(value.additional_fields || {}) },
    address: value.address || '',
    description: value.description || '',
    meeting_url: value.meeting_url || '',
  });
};

const errorMessage = error =>
  error?.response?.data?.error || t('CONVERSATION.APPOINTMENT.ERROR');

const loadOptions = async () => {
  isLoadingOptions.value = true;
  try {
    const { data } = await ConversationApi.getAppointmentOptions(
      props.conversationId
    );
    options.value = data;
    crmModule.value = data.crm_module || '';
    currentAppointment.value = data.current_appointment || null;
    appointmentHistory.value = data.appointment_history || [];
    const defaults = Object.fromEntries(
      [
        ...(data.required_contact_fields || []),
        ...(data.optional_contact_fields || []),
      ].map(field => [field.name, field.default || ''])
    );
    if (currentAppointment.value) {
      mode.value = 'edit';
      loadAppointment(currentAppointment.value);
    } else {
      mode.value = crmModule.value === 'Leads' ? 'convert' : 'create';
      appointment.event_title = data.event_title || props.agentName;
      appointment.email = data.email || props.contactEmail;
      appointment.additional_fields = defaults;
    }
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isLoadingOptions.value = false;
  }
};

watch(
  () => props.show,
  async isOpen => {
    if (!isOpen) return;

    resetForm();
    await loadOptions();
  }
);

const close = () => {
  if (!isSaving.value) {
    localShow.value = false;
    emit('cancel');
  }
};

const picklistOptions = name => options.value.picklists?.[name] || [];

const isPicklistField = field => field.picklist_values?.length > 0;

const inputTypeFor = field => {
  if (['date', 'time', 'email', 'number'].includes(field.type)) {
    return field.type;
  }
  return 'text';
};

const beginReprogram = () => {
  mode.value = 'reprogram';
  appointment.date = '';
  appointment.time = '';
  appointment.status = 'Reprogramada';
};

const cancelReprogram = () => {
  mode.value = 'edit';
  loadAppointment(currentAppointment.value || {});
};

const submitLabel = computed(() => {
  if (isReprogramming.value) {
    return t('CONVERSATION.APPOINTMENT.REPROGRAM_SAVE');
  }
  if (mode.value === 'convert') {
    return t('CONVERSATION.APPOINTMENT.CONVERT_SAVE');
  }
  if (isEditingExisting.value) {
    return t('CONVERSATION.APPOINTMENT.UPDATE_SAVE');
  }
  return t('CONVERSATION.APPOINTMENT.SAVE');
});

const saveAppointment = async () => {
  if (isSaving.value) {
    return;
  }

  isSaving.value = true;
  try {
    const { data } = await ConversationApi.createAppointment(
      props.conversationId,
      { ...appointment, mode: mode.value }
    );
    useAlert(
      data.calendar_invitation?.queued
        ? t('CONVERSATION.APPOINTMENT.SUCCESS_WITH_CALENDAR')
        : t('CONVERSATION.APPOINTMENT.SUCCESS')
    );
    emit('updated', data);
    localShow.value = false;
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <woot-modal
    v-model:show="localShow"
    :on-close="close"
    :close-on-backdrop-click="!isSaving"
    class="!items-start [&>div]:!top-12 [&>div]:sticky"
  >
    <form
      class="flex w-full flex-col gap-5 px-6 py-6"
      @submit.prevent="saveAppointment"
    >
      <div>
        <h3
          id="appointment-scheduler-title"
          class="text-lg font-semibold text-n-slate-12"
        >
          {{ appointmentTitle }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('CONVERSATION.APPOINTMENT.DESCRIPTION') }}
        </p>
        <p class="mt-1 text-xs text-n-slate-11">
          {{
            t('CONVERSATION.APPOINTMENT.TIMEZONE', { timezone: agentTimezone })
          }}
          · {{ t('CONVERSATION.APPOINTMENT.DURATION') }}
        </p>
      </div>

      <section
        v-if="appointmentHistory.length"
        class="rounded-lg border border-n-weak bg-n-alpha-2 p-4"
      >
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ t('CONVERSATION.APPOINTMENT.HISTORY_TITLE') }}
        </h4>
        <div class="mt-3 max-h-44 space-y-3 overflow-y-auto">
          <article
            v-for="item in appointmentHistory"
            :key="item.message_id"
            class="border-l-2 border-n-weak pl-3 text-xs text-n-slate-11"
          >
            <p class="font-medium text-n-slate-12">
              {{ item.date }} · {{ item.time }} · {{ item.status }}
            </p>
            <p v-if="item.action === 'reprogrammed' && item.previous">
              {{
                t('CONVERSATION.APPOINTMENT.HISTORY_REPROGRAMMED', {
                  date: item.previous.date,
                  time: item.previous.time,
                })
              }}
            </p>
            <p>{{ item.updated_by }} · {{ item.created_at }}</p>
          </article>
        </div>
      </section>

      <div
        v-if="currentAppointment?.requires_status_update && !isReprogramming"
        class="rounded-lg border border-n-ruby-5 bg-n-ruby-2 p-4 text-sm text-n-ruby-11"
      >
        {{ t('CONVERSATION.APPOINTMENT.EXPIRED_STATUS_REQUIRED') }}
      </div>

      <template v-if="isReprogramming">
        <div class="rounded-lg border border-n-weak bg-n-alpha-2 p-4 text-sm">
          <p class="font-medium text-n-slate-12">
            {{ t('CONVERSATION.APPOINTMENT.CURRENT_SCHEDULE') }}
          </p>
          <p class="mt-1 text-n-slate-11">
            {{ currentAppointment?.date }} · {{ currentAppointment?.time }} ·
            {{ currentAppointment?.status }}
          </p>
          <p class="mt-2 text-xs text-n-slate-11">
            {{ t('CONVERSATION.APPOINTMENT.REPROGRAM_ONLY_FIELDS') }}
          </p>
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.APPOINTMENT.DATE') }}
            <input
              v-model="appointment.date"
              required
              type="date"
              class="!mb-0 !h-10 !w-full !rounded-lg !bg-n-alpha-black2 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
            />
          </label>
          <label
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.APPOINTMENT.TIME') }}
            <input
              v-model="appointment.time"
              required
              type="time"
              class="!mb-0 !h-10 !w-full !rounded-lg !bg-n-alpha-black2 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
            />
          </label>
        </div>

        <label
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CONVERSATION.APPOINTMENT.STATUS') }}
          <input
            value="Reprogramada"
            readonly
            type="text"
            class="!mb-0 !h-10 !w-full cursor-not-allowed !rounded-lg !bg-n-alpha-2 !px-3 !py-2 !text-sm text-n-slate-12 !outline-n-strong -outline-offset-1"
          />
        </label>
      </template>

      <template v-else>
        <label
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CONVERSATION.APPOINTMENT.EVENT_TITLE_LABEL') }}
          <input
            v-model="appointment.event_title"
            required
            type="text"
            class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12 !h-10"
          />
        </label>

        <label
          v-if="isFormFieldVisible('email')"
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CONVERSATION.APPOINTMENT.EMAIL') }}
          <input
            v-model="appointment.email"
            required
            type="email"
            class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12 !h-10"
            :placeholder="t('CONVERSATION.APPOINTMENT.EMAIL_PLACEHOLDER')"
          />
        </label>

        <div
          v-if="
            isFormFieldVisible('appointment_date') ||
            isFormFieldVisible('appointment_time')
          "
          class="grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          <label
            v-if="isFormFieldVisible('appointment_date')"
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.APPOINTMENT.DATE') }}
            <input
              v-model="appointment.date"
              required
              type="date"
              :readonly="isEditingExisting"
              class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12 !h-10"
            />
          </label>
          <label
            v-if="isFormFieldVisible('appointment_time')"
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.APPOINTMENT.TIME') }}
            <input
              v-model="appointment.time"
              required
              type="time"
              :readonly="isEditingExisting"
              class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12 !h-10"
            />
          </label>
        </div>

        <div
          v-if="
            isFormFieldVisible('appointment_status') ||
            isFormFieldVisible('appointment_type')
          "
          class="grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          <label
            v-if="isFormFieldVisible('appointment_status')"
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.APPOINTMENT.STATUS') }}
            <select
              v-model="appointment.status"
              required
              class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12 !h-10"
            >
              <option disabled value="">
                {{ t('CONVERSATION.APPOINTMENT.SELECT_OPTION') }}
              </option>
              <option
                v-for="status in picklistOptions('statuses')"
                :key="status"
                :value="status"
              >
                {{ status }}
              </option>
            </select>
          </label>
          <label
            v-if="isFormFieldVisible('appointment_type')"
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.APPOINTMENT.TYPE') }}
            <select
              v-model="appointment.appointment_type"
              required
              class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12 !h-10"
            >
              <option disabled value="">
                {{ t('CONVERSATION.APPOINTMENT.SELECT_OPTION') }}
              </option>
              <option
                v-for="appointmentType in picklistOptions('appointment_types')"
                :key="appointmentType"
                :value="appointmentType"
              >
                {{ appointmentType }}
              </option>
            </select>
          </label>
        </div>

        <div
          v-if="
            isFormFieldVisible('appointment_generated_by') ||
            requiredContactFields.length
          "
        >
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('CONVERSATION.APPOINTMENT.VTIGER_DETAILS') }}
          </p>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ t('CONVERSATION.APPOINTMENT.VTIGER_DETAILS_HELP') }}
          </p>
        </div>

        <div
          v-if="
            isFormFieldVisible('appointment_generated_by') ||
            requiredContactFields.length
          "
          class="grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          <label
            v-if="isFormFieldVisible('appointment_generated_by')"
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CONVERSATION.APPOINTMENT.GENERATED_BY') }}
            <select
              v-model="appointment.generated_by"
              required
              class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12 !h-10"
            >
              <option disabled value="">
                {{ t('CONVERSATION.APPOINTMENT.SELECT_OPTION') }}
              </option>
              <option
                v-for="option in picklistOptions('generated_by')"
                :key="option"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
          </label>
          <label
            v-for="field in requiredContactFields"
            :key="field.name"
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ field.label }}
            <select
              v-if="isPicklistField(field)"
              v-model="appointment.additional_fields[field.name]"
              :required="field.required !== false"
              class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12 !h-10"
            >
              <option disabled value="">
                {{ t('CONVERSATION.APPOINTMENT.SELECT_OPTION') }}
              </option>
              <option
                v-for="option in field.picklist_values"
                :key="option"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
            <input
              v-else
              v-model="appointment.additional_fields[field.name]"
              :required="field.required !== false"
              :type="inputTypeFor(field)"
              class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12 !h-10"
            />
          </label>
        </div>

        <section
          v-if="optionalContactFields.length"
          class="rounded-lg border border-n-weak bg-n-alpha-2"
        >
          <button
            type="button"
            class="flex w-full items-center justify-between gap-3 p-4 text-left"
            :aria-expanded="showOptionalFields"
            @click="showOptionalFields = !showOptionalFields"
          >
            <span>
              <span class="block text-sm font-medium text-n-slate-12">
                {{
                  t('CONVERSATION.APPOINTMENT.OPTIONAL_VTIGER_DETAILS', {
                    count: optionalContactFields.length,
                  })
                }}
              </span>
              <span class="mt-1 block text-xs text-n-slate-11">
                {{ t('CONVERSATION.APPOINTMENT.OPTIONAL_VTIGER_DETAILS_HELP') }}
              </span>
            </span>
            <span
              class="size-5 shrink-0 i-lucide-chevron-down transition-transform duration-200"
              :class="{ 'rotate-180': showOptionalFields }"
            />
          </button>

          <div
            v-if="showOptionalFields"
            class="grid grid-cols-1 gap-4 border-t border-n-weak p-4 sm:grid-cols-2"
          >
            <label
              v-for="field in optionalContactFields"
              :key="field.name"
              class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
            >
              {{ field.label }}
              <select
                v-if="isPicklistField(field)"
                v-model="appointment.additional_fields[field.name]"
                class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12 !h-10"
              >
                <option value="">
                  {{ t('CONVERSATION.APPOINTMENT.SELECT_OPTION') }}
                </option>
                <option
                  v-for="option in field.picklist_values"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
              <input
                v-else
                v-model="appointment.additional_fields[field.name]"
                :type="inputTypeFor(field)"
                class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12 !h-10"
              />
            </label>
          </div>
        </section>

        <label
          v-if="isFormFieldVisible('address')"
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CONVERSATION.APPOINTMENT.ADDRESS') }}
          <textarea
            v-model="appointment.address"
            rows="3"
            class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12"
            :placeholder="t('CONVERSATION.APPOINTMENT.ADDRESS_PLACEHOLDER')"
          />
          <span class="text-xs font-normal text-n-slate-11">
            {{ t('CONVERSATION.APPOINTMENT.ADDRESS_HELP') }}
          </span>
        </label>

        <label
          v-if="isFormFieldVisible('appointment_description')"
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CONVERSATION.APPOINTMENT.EVENT_DESCRIPTION') }}
          <textarea
            v-model="appointment.description"
            required
            rows="3"
            class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12"
            :placeholder="
              t('CONVERSATION.APPOINTMENT.EVENT_DESCRIPTION_PLACEHOLDER')
            "
          />
        </label>

        <label
          v-if="isFormFieldVisible('appointment_meeting_url')"
          class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CONVERSATION.APPOINTMENT.MEETING_URL') }}
          <input
            v-model="appointment.meeting_url"
            required
            type="url"
            class="!w-full !mb-0 !rounded-lg !bg-n-alpha-black2 !outline-n-strong -outline-offset-1 !px-3 !py-2 !text-sm text-n-slate-12 !h-10"
            :placeholder="t('CONVERSATION.APPOINTMENT.MEETING_URL_PLACEHOLDER')"
          />
        </label>
      </template>

      <div class="flex items-center justify-end gap-3">
        <NextButton
          v-if="isReprogramming"
          faded
          slate
          type="button"
          :label="t('CONVERSATION.APPOINTMENT.BACK_TO_EDITOR')"
          :disabled="isSaving"
          @click="cancelReprogram"
        />
        <NextButton
          v-if="isEditingExisting"
          faded
          slate
          type="button"
          icon="i-lucide-calendar-sync"
          :label="t('CONVERSATION.APPOINTMENT.REPROGRAM_BUTTON')"
          :disabled="isSaving"
          @click="beginReprogram"
        />
        <NextButton
          faded
          slate
          type="button"
          :label="t('CONVERSATION.APPOINTMENT.CANCEL')"
          :disabled="isSaving"
          @click="close"
        />
        <NextButton
          type="submit"
          icon="i-lucide-calendar-plus"
          :label="submitLabel"
          :is-loading="isSaving || isLoadingOptions"
          :disabled="isSaving || isLoadingOptions"
        />
      </div>
    </form>
  </woot-modal>
</template>
